import { API_BASE } from '../env'
import { request } from './http'
import type { Schema } from './schema'

// ── Administration: users, roles, brand access and permissions ───────────────────────────────────
//
// Response shapes are taken from the server's own handlers rather than from the Blazor client's
// declared return types, because the two disagree in ways that matter. Three groups behave
// differently and typing them alike would produce a page of `undefined` against a server answering
// perfectly — the same trap Docker's PascalCase payloads set earlier in this port:
//
//   * `/admin/users` and `/admin/roles` answer with an ENVELOPE — `{ users }` / `{ roles }`.
//   * `/admin/permissions/definitions`, `/admin/users/{id}/permissions`, the permission audit log
//     and both access-review lists answer with a BARE ARRAY or object, no envelope at all.
//   * `/admin/roles/{id}/permissions` answers with `{ permission_keys }`, and takes the same shape
//     back on PUT.

/** A row of `/admin/users`. Deliberately narrower than the server's `User`: no password hash. */
export type AdminUserWire = Schema<'AdminUserWire'>

export interface AdminUserAccessWire {
  brand_id: string
  brand_name: string
  role_id: string
  role_name: string
}

/**
 * A role.
 *
 * **The ten `can_*` booleans are a second, older permission model living beside the 59-key one.**
 * The server still requires them on create and update, and `Roles.razor` still sends them while
 * offering no UI for any of them — so they are carried faithfully here rather than dropped, and
 * whether the two models should be merged is a product decision this rewrite deliberately does not
 * take.
 */
export type AdminRoleWire = Schema<'Role'>

export interface AdminBrandWire {
  id: string
  name: string
  description?: string | null
  /**
   * Always true in practice — `GetBrandsAsync` filters `WHERE is_enabled = TRUE`, so a soft-deleted
   * brand never appears in this list. Carried anyway so an update sends the brand's real state
   * rather than assuming; see `adminUpdateBrand`.
   */
  is_enabled: boolean
  /** Whether an expected device may enroll into this brand with no claim (plan D6). */
  claimless_enrollment?: boolean
  /** Source addresses allowed to do so; empty or absent means anywhere. */
  claimless_ip_allow?: string[]
  /** Days a browser that gave the code is remembered; null follows the global setting (Plans/sign-in-remembered-browsers.md). */
  remember_browser_days?: number | null
  /** Everyone who reaches the brand gives a code at sign-in. */
  require_second_factor?: boolean
  /** A fresh code before remote control of the brand's devices. */
  remote_control_check?: boolean
}

/** What the mutating admin endpoints answer with. The callers read the status, not the body. */
export type AdminMessageWire = Schema<'AdminMessageWire'>

export type EffectivePermissionsWire = Schema<'EffectivePermissions'>

export interface UserPermissionWire {
  id: string
  user_id: string
  permission_key: string
  brand_id?: string | null
  brand_name?: string | null
  granted_by?: string | null
  granted_by_name?: string | null
  granted_reason?: string | null
  expires_at?: string | null
  last_used_at?: string | null
  created_at: string
  display_name: string
  description: string
  category: string
  is_dangerous: boolean
  is_expired: boolean
}

export type PermissionAuditEntryWire = Schema<'PermissionAuditEntryWithNames'>

/**
 * A user the six-month access review is overdue for.
 *
 * **`last_login_at` is what the server sends, and it is NOT the last review date.** The query
 * selecting these users is correct — `last_access_review_at IS NULL OR < NOW() - INTERVAL '6
 * months'` — but the handler then returns the user's last *login*, and the Blazor client renders it
 * as "N days since last review". A user reviewed eight months ago who signs in daily shows as zero
 * days and no warning colour, which understates overdue accounts on a PCI-DSS 7.2.2 control. The
 * field is named for what it actually is here, and the page presents it as a login date rather than
 * repeating the claim.
 */
export type UserDueForReviewWire = Schema<'UserDueForReviewWire'>

export type AccessReviewWire = Schema<'AccessReviewWithNames'>

export function adminGetUsers(): Promise<Schema<'AdminUserListWire'>> {
  return request<Schema<'AdminUserListWire'>>('GET', `${API_BASE}/admin/users`)
}

export function adminDeleteUser(userId: string): Promise<AdminMessageWire> {
  return request<AdminMessageWire>('DELETE', `${API_BASE}/admin/users/${userId}`)
}

export function adminGetUserAccess(userId: string): Promise<{ access: AdminUserAccessWire[] }> {
  return request<{ access: AdminUserAccessWire[] }>('GET', `${API_BASE}/admin/users/${userId}/access`)
}

export function adminGrantUserAccess(
  userId: string,
  brandId: string,
  roleId: string,
): Promise<AdminMessageWire> {
  return request<AdminMessageWire>('POST', `${API_BASE}/admin/users/${userId}/access`, {
    json: { brand_id: brandId, role_id: roleId },
  })
}

export function adminRevokeUserAccess(
  userId: string,
  brandId: string,
): Promise<AdminMessageWire> {
  return request<AdminMessageWire>('DELETE', `${API_BASE}/admin/users/${userId}/access/${brandId}`)
}

export function adminGetRoles(): Promise<Schema<'AdminRoleListWire'>> {
  return request<Schema<'AdminRoleListWire'>>('GET', `${API_BASE}/admin/roles`)
}

/** The ten booleans are required by the server; see `AdminRoleWire`. */
export type RoleFlagsWire = Omit<AdminRoleWire, 'id' | 'is_system_role' | 'created_at' | 'updated_at'>

export function adminCreateRole(body: RoleFlagsWire): Promise<AdminRoleWire> {
  return request<AdminRoleWire>('POST', `${API_BASE}/admin/roles`, { json: body })
}

export function adminUpdateRole(roleId: string, body: RoleFlagsWire): Promise<AdminMessageWire> {
  return request<AdminMessageWire>('PUT', `${API_BASE}/admin/roles/${roleId}`, { json: body })
}

export function adminDeleteRole(roleId: string): Promise<AdminMessageWire> {
  return request<AdminMessageWire>('DELETE', `${API_BASE}/admin/roles/${roleId}`)
}

export function adminGetRolePermissions(roleId: string): Promise<Schema<'RolePermissionsWire'>> {
  return request<Schema<'RolePermissionsWire'>>(
    'GET',
    `${API_BASE}/admin/roles/${roleId}/permissions`,
  )
}

export function adminSetRolePermissions(
  roleId: string,
  permissionKeys: string[],
): Promise<AdminMessageWire> {
  return request<AdminMessageWire>('PUT', `${API_BASE}/admin/roles/${roleId}/permissions`, {
    json: { permission_keys: permissionKeys },
  })
}

export function adminGetBrands(): Promise<{ brands: AdminBrandWire[] }> {
  return request<{ brands: AdminBrandWire[] }>('GET', `${API_BASE}/admin/brands`)
}

/**
 * One of the 59 permissions the server seeds, across 15 categories.
 *
 * The set grows by migration — 0013 added docker, 0014 and 0018 jobs, 0021
 * `device.connect.video` — so the client reads it rather than carrying a copy that would disagree
 * with the database the day after the next one.
 *
 * - `requires_scope`: Whether the grant can be narrowed to one brand rather than being global.
 * - `is_dangerous`: Destructive. Shown with a warning marker wherever it is offered.
 * - `requires_admin`: Only an administrator may grant it.
 */
export type PermissionDefinitionWire = Schema<'PermissionDefinition'>

/** Bare array, not an envelope. */
export function adminGetPermissionDefinitions(): Promise<PermissionDefinitionWire[]> {
  return request<PermissionDefinitionWire[]>('GET', `${API_BASE}/admin/permissions/definitions`)
}

export function adminGetUserPermissions(userId: string): Promise<EffectivePermissionsWire> {
  return request<EffectivePermissionsWire>(
    'GET',
    `${API_BASE}/admin/users/${userId}/permissions`,
  )
}

/**
 * Grants one permission.
 *
 * The response is `{ id, success }` — **not** `{ permission_id }`, which is what the Blazor client
 * reads with `GetValueOrDefault("permission_id")`, so it has been receiving null for the new id on
 * every successful grant.
 */
export function adminGrantPermission(
  userId: string,
  body: {
    permission_key: string
    brand_id?: string | null
    reason?: string | null
    expires_at?: string | null
  },
): Promise<Schema<'AdminCreatedIdWire'>> {
  return request<Schema<'AdminCreatedIdWire'>>(
    'POST',
    `${API_BASE}/admin/users/${userId}/permissions`,
    { json: body },
  )
}

export function adminRevokePermission(
  userId: string,
  permissionId: string,
): Promise<Schema<'SuccessWire'>> {
  return request<Schema<'SuccessWire'>>(
    'DELETE',
    `${API_BASE}/admin/users/${userId}/permissions/${permissionId}`,
  )
}

export function adminGetPermissionAudit(userId: string): Promise<PermissionAuditEntryWire[]> {
  return request<PermissionAuditEntryWire[]>(
    'GET',
    `${API_BASE}/admin/users/${userId}/permissions/audit`,
  )
}

export function adminGetAccessReviewsDue(): Promise<UserDueForReviewWire[]> {
  return request<UserDueForReviewWire[]>('GET', `${API_BASE}/admin/access-reviews/due`)
}

export function adminGetUserAccessReviews(userId: string): Promise<AccessReviewWire[]> {
  return request<AccessReviewWire[]>('GET', `${API_BASE}/admin/users/${userId}/access-review`)
}

/** Also `{ id, success }`, not the `{ review_id }` the Blazor client reads. */
export function adminRecordAccessReview(
  userId: string,
  body: { permissions_confirmed: boolean; changes_made?: string | null; notes?: string | null },
): Promise<Schema<'AdminCreatedIdWire'>> {
  return request<Schema<'AdminCreatedIdWire'>>(
    'POST',
    `${API_BASE}/admin/users/${userId}/access-review`,
    { json: body },
  )
}
